import { useState, useCallback, useEffect } from 'react'
import { useApiFetch } from '../../hooks/useApiFetch.js'
import { useAdminStore } from '../../stores/adminStore.js'
import { StatCard, Select, Input, Button, Table, Pagination, Alert, type TableColumn } from '../../components/ui/index.js'
import { StatusBadge } from '../../components/admin/index.js'
import { formatDate, formatCurrency, formatNumber, formatCredits } from '../../lib/format.js'
import styles from './AdminPayments.module.css'

interface Payment {
  id: string
  user: { id: string; email: string; name: string | null }
  type: string
  method: string
  amount: number
  creditsGranted: number | null
  status: string
  orderId: string | null
  createdAt: string
}

interface PaymentsResponse {
  payments: Payment[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
  summary: { totalAmount: number; count: number }
}

const TYPE_LABELS: Record<string, string> = {
  PREMIUM_UPGRADE: '프리미엄',
  AI_CREDITS: 'AI 크레딧',
  AI_CREDITS_BUNDLE: '크레딧 번들',
}

export function AdminPayments() {
  const { filters, setFilters, resetFilters, selectedUserId, setSelectedUserId } = useAdminStore()
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  useEffect(() => {
    resetFilters()
  }, [resetFilters])

  const params = new URLSearchParams()
  params.set('page', String(filters.page))
  params.set('pageSize', String(filters.pageSize))
  params.set('sortBy', filters.sortBy)
  params.set('sortOrder', filters.sortOrder)
  if (statusFilter) params.set('status', statusFilter)
  if (typeFilter) params.set('type', typeFilter)
  if (selectedUserId) params.set('userId', selectedUserId)
  if (startDate) params.set('startDate', startDate)
  if (endDate) params.set('endDate', endDate)

  const { data, isLoading, error } = useApiFetch<PaymentsResponse>(
    `/api/admin/payments?${params.toString()}`
  )

  const handleSort = useCallback((key: string, order: 'asc' | 'desc') => {
    setFilters({ sortBy: key, sortOrder: order })
  }, [setFilters])

  const columns: TableColumn<Payment>[] = [
    { key: 'id', label: 'ID', render: (row) => row.id.slice(0, 8), width: '80px' },
    { key: 'user', label: '유저', render: (row) => row.user.name ?? row.user.email },
    { key: 'type', label: '타입', render: (row) => <StatusBadge status={row.type} label={TYPE_LABELS[row.type] ?? row.type} />, width: '100px' },
    { key: 'method', label: '결제수단', width: '90px' },
    { key: 'amount', label: '금액', sortable: true, render: (row) => formatCurrency(row.amount), align: 'right' as const, width: '110px' },
    { key: 'creditsGranted', label: '크레딧', render: (row) => row.creditsGranted != null ? formatCredits(row.creditsGranted) : '-', align: 'right' as const, width: '80px' },
    { key: 'status', label: '상태', sortable: true, render: (row) => <StatusBadge status={row.status} />, width: '90px' },
    { key: 'orderId', label: '주문ID', render: (row) => row.orderId ? row.orderId.slice(0, 12) : '-', width: '110px' },
    { key: 'createdAt', label: '일시', sortable: true, render: (row) => formatDate(row.createdAt, 'yyyy.M.d HH:mm'), width: '130px' },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.statGrid}>
        <StatCard label="총 금액" value={formatCurrency(data?.summary.totalAmount ?? 0)} />
        <StatCard label="결제 건수" value={`${formatNumber(data?.summary.count ?? 0)}건`} />
      </div>

      <div className={styles.filters}>
        <Select
          size="sm"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setFilters({ page: 1 }) }}
          options={[
            { value: '', label: '전체 상태' },
            { value: 'PENDING', label: 'PENDING' },
            { value: 'COMPLETED', label: 'COMPLETED' },
            { value: 'FAILED', label: 'FAILED' },
            { value: 'REFUNDED', label: 'REFUNDED' },
          ]}
        />
        <Select
          size="sm"
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setFilters({ page: 1 }) }}
          options={[
            { value: '', label: '전체 타입' },
            { value: 'PREMIUM_UPGRADE', label: '프리미엄' },
            { value: 'AI_CREDITS', label: 'AI 크레딧' },
            { value: 'AI_CREDITS_BUNDLE', label: '크레딧 번들' },
          ]}
        />
        <Input
          size="sm"
          type="date"
          value={startDate}
          onChange={(e) => { setStartDate(e.target.value); setFilters({ page: 1 }) }}
        />
        <Input
          size="sm"
          type="date"
          value={endDate}
          onChange={(e) => { setEndDate(e.target.value); setFilters({ page: 1 }) }}
        />
        {selectedUserId && (
          <Button variant="ghost" size="sm" onClick={() => setSelectedUserId(null)}>
            유저 필터 해제
          </Button>
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table<Payment>
        columns={columns}
        data={data?.payments ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSort}
      />

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination
          current={data.pagination.page}
          total={data.pagination.totalPages}
          onChange={(page) => setFilters({ page })}
          size="sm"
        />
      )}
    </div>
  )
}
